// Los errores de Supabase Auth, en castellano.
//
// Nace para `/alta-empresa` (el alta+pago por link), que mostraba `err.message` CRUDO:
// alguien que erraba la contraseña en el paso que cobra leía "Invalid login
// credentials", y quien usaba la solapa de registro con un mail que ya tenía cuenta
// leía "User already registered". Es el paso más caro del embudo (2 de cada 3 no llegan
// a crear la cuenta) y estaba en inglés.
// Ver ADR-2026-09-22-los-errores-de-auth-del-alta-por-link-estan-en-castellano.
//
// Los dos errores que el resto de la app ya traducía —credenciales inválidas y mail sin
// confirmar— llevan el texto COPIADO LITERAL de `AuthForm.tsx` y `AuthCompat.tsx`, para
// que la app diga lo mismo en todos lados. Los demás son nuevos: nadie los traducía.
// Esas pantallas todavía traducen inline a propósito: migrarlas es un cambio con su
// propio riesgo (son el login de toda la app) y sin test posible, porque no hay DOM en
// los tests. Cuando se haga, el destino es esta función.

/** Texto de un error de Supabase, venga como `Error`, como string o como objeto. */
function textoDelError(err: unknown): string {
  if (typeof err === 'string') return err;
  if (err instanceof Error) return err.message ?? '';
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message?: unknown }).message ?? '');
  }
  return '';
}

/**
 * ¿Es el error de "ese mail ya tiene cuenta"? Se pregunta aparte porque la respuesta
 * correcta no es un mensaje sino una ACCIÓN: llevarlo a iniciar sesión con el mail que
 * ya escribió. Mandarlo a "revisá tu correo" sería mentirle — este proyecto no pide
 * confirmar el mail (verificado: las 346 altas de septiembre quedaron confirmadas al
 * instante y no se envió ningún mail de confirmación), así que Supabase devuelve este
 * error en vez de la cuenta ofuscada que devolvería con confirmación activada.
 */
export function esMailYaRegistrado(err: unknown): boolean {
  const t = textoDelError(err).toLowerCase();
  return t.includes('already registered') || t.includes('already been registered');
}

/**
 * El error, listo para mostrar. Lo desconocido vuelve tal cual: es preferible un texto
 * raro en inglés a "ocurrió un error" —que no le dice nada a nadie— cuando alguien nos
 * escribe para contar qué le pasó.
 */
export function mensajeDeErrorDeAuth(err: unknown): string {
  const texto = textoDelError(err).trim();
  const t = texto.toLowerCase();

  // Mismo texto que `AuthForm.tsx` y `AuthCompat.tsx`.
  if (t.includes('invalid login credentials'))
    return 'Correo electrónico o contraseña incorrectos';

  // Mismo texto que `AuthForm.tsx`.
  if (t.includes('email not confirmed'))
    return 'Tu email todavía no está confirmado. Revisá tu bandeja de entrada.';

  if (esMailYaRegistrado(err))
    return 'Ya existe una cuenta con ese correo. Iniciá sesión para continuar con tu suscripción.';

  // SIN el número: el mensaje de GoTrue trae el mínimo REAL del proyecto ("Password
  // should be at least N characters"), y escribir un 6 acá sería prometer un piso que
  // el servidor puede no tener. Un mensaje que miente en la pantalla que cobra es peor
  // que uno vago.
  if (t.includes('password should be at least') || t.includes('password is too short'))
    return 'Esa contraseña es muy corta. Probá con una más larga.';

  if (t.includes('unable to validate email address') || t.includes('invalid email'))
    return 'Ese correo no parece válido. Revisalo y probá de nuevo.';

  // Supabase limita los intentos por IP: "For security purposes, you can only request
  // this after N seconds" / "email rate limit exceeded".
  if (t.includes('for security purposes') || t.includes('rate limit'))
    return 'Probaste varias veces seguidas. Esperá un momento y volvé a intentar.';

  if (t.includes('failed to fetch') || t.includes('network'))
    return 'No pudimos conectarnos. Revisá tu conexión y probá de nuevo.';

  return texto || 'No se pudo continuar. Reintentá.';
}
